import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  MONTH_CHOICES,
  PAYMENT_STATUS_CHOICES,
  PAYMENT_MODE_CHOICES,
  PAYMENT_CURRENCY_CHOICES,
} from '../../utils/payrollUtils';
import FormEmployeeSearchField from '../common/FormEmployeeSearchField';

const PayrollForm = ({ open, mode, formData, formErrors, initialEmployee, onClose, onSubmit, onInputChange }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {mode === 'add' ? 'Add New Payroll' : mode === 'edit' ? 'Edit Payroll' : 'View Payroll'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" color="primary" gutterBottom>
              Employee & Payment Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormEmployeeSearchField
                  value={formData.employee}
                  onChange={(id) => onInputChange('employee', id || '')}
                  label="Employee"
                  required
                  disabled={mode === 'view'}
                  error={!!formErrors.employee}
                  helperText={formErrors.employee}
                  initialEmployee={initialEmployee}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth error={!!formErrors.month} disabled={mode === 'view'}>
                  <InputLabel>Month *</InputLabel>
                  <Select
                    value={formData.month}
                    label="Month *"
                    onChange={(e) => onInputChange('month', e.target.value)}
                  >
                    {MONTH_CHOICES.map((month) => (
                      <MenuItem key={month} value={month}>
                        {month}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.month && (
                    <Typography variant="caption" color="error">
                      {formErrors.month}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Year *"
                  type="number"
                  value={formData.year}
                  onChange={(e) => onInputChange('year', Math.max(0, parseInt(e.target.value, 10) || 0))}
                  inputProps={{ min: 0, onFocus: (e) => e.target.select() }}
                  disabled={mode === 'view'}
                  error={!!formErrors.year}
                  helperText={formErrors.year}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth error={!!formErrors.payment_status} disabled={mode === 'view'}>
                  <InputLabel>Payment Status</InputLabel>
                  <Select
                    value={formData.payment_status}
                    label="Payment Status"
                    onChange={(e) => onInputChange('payment_status', e.target.value)}
                  >
                    {PAYMENT_STATUS_CHOICES.map((status) => (
                      <MenuItem key={status.value} value={status.value}>
                        {status.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.payment_status && (
                    <Typography variant="caption" color="error">
                      {formErrors.payment_status}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth error={!!formErrors.payment_currency} disabled={mode === 'view'}>
                  <InputLabel>Payment Currency</InputLabel>
                  <Select
                    value={formData.payment_currency}
                    label="Payment Currency"
                    onChange={(e) => onInputChange('payment_currency', e.target.value)}
                  >
                    {PAYMENT_CURRENCY_CHOICES.map((currency) => (
                      <MenuItem key={currency} value={currency}>
                        {currency}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.payment_currency && (
                    <Typography variant="caption" color="error">
                      {formErrors.payment_currency}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <FormControl fullWidth error={!!formErrors.payment_mode} disabled={mode === 'view'}>
                  <InputLabel>Payment Mode</InputLabel>
                  <Select
                    value={formData.payment_mode}
                    label="Payment Mode"
                    onChange={(e) => onInputChange('payment_mode', e.target.value)}
                  >
                    {PAYMENT_MODE_CHOICES.map((mode) => (
                      <MenuItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {formErrors.payment_mode && (
                    <Typography variant="caption" color="error">
                      {formErrors.payment_mode}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Payment Date"
                    value={formData.payment_date ? dayjs(formData.payment_date) : null}
                    onChange={(newValue) =>
                      onInputChange('payment_date', newValue ? newValue.format('YYYY-MM-DD') : '')
                    }
                    disabled={mode === 'view'}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!formErrors.payment_date,
                        helperText: formErrors.payment_date,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" color="primary" gutterBottom>
              Salary & Allowances
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Basic Salary *"
                  type="number"
                  value={formData.basic_salary}
                  onChange={(e) => onInputChange('basic_salary', Math.max(0, parseInt(e.target.value, 10) || 0))}
                  inputProps={{ min: 0, onFocus: (e) => e.target.select() }}
                  disabled={mode === 'view'}
                  error={!!formErrors.basic_salary}
                  helperText={formErrors.basic_salary}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Transport Allowance"
                  type="number"
                  value={formData.transport_allowance}
                  onChange={(e) =>
                    onInputChange(
                      'transport_allowance',
                      e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                    )
                  }
                  inputProps={{ min: 0, onFocus: (e) => e.target.select() }}
                  disabled={mode === 'view'}
                  error={!!formErrors.transport_allowance}
                  helperText={formErrors.transport_allowance}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Bonus Dues"
                  type="number"
                  value={formData.bonus_dues}
                  onChange={(e) =>
                    onInputChange('bonus_dues', e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10)))
                  }
                  disabled={mode === 'view'}
                  error={!!formErrors.bonus_dues}
                  helperText={formErrors.bonus_dues}
                  inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Weekends Pay"
                  type="number"
                  value={formData.weekends_pay}
                  onChange={(e) =>
                    onInputChange(
                      'weekends_pay',
                      e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                    )
                  }
                  disabled={mode === 'view'}
                  error={!!formErrors.weekends_pay}
                  helperText={formErrors.weekends_pay}
                  inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Worked Days *"
                  type="number"
                  value={formData.worked_days}
                  onChange={(e) =>
                    onInputChange(
                      'worked_days',
                      e.target.value === '' ? '' : Math.max(0, parseFloat(e.target.value) || 0)
                    )
                  }
                  inputProps={{ min: 0, step: 0.1, onFocus: (e) => e.target.select() }}
                  disabled={mode === 'view'}
                  error={!!formErrors.worked_days}
                  helperText={formErrors.worked_days}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Gross Payable"
                  type="number"
                  value={formData.gross_payable}
                  disabled
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 'bold',
                      color: 'success.main',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" color="primary" gutterBottom>
              Deductions
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Absent Deductions"
                  type="number"
                  value={formData.absent_deductions}
                  onChange={(e) =>
                    onInputChange(
                      'absent_deductions',
                      e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                    )
                  }
                  disabled={mode === 'view'}
                  error={!!formErrors.absent_deductions}
                  helperText={formErrors.absent_deductions}
                  inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Loan Deductions"
                  type="number"
                  value={formData.loan_deductions}
                  onChange={(e) =>
                    onInputChange(
                      'loan_deductions',
                      e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                    )
                  }
                  disabled={mode === 'view'}
                  error={!!formErrors.loan_deductions}
                  helperText={formErrors.loan_deductions}
                  inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Tax Deductions"
                  type="number"
                  value={formData.tax_deductions}
                  onChange={(e) =>
                    onInputChange(
                      'tax_deductions',
                      e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                    )
                  }
                  disabled={mode === 'view'}
                  error={!!formErrors.tax_deductions}
                  helperText={formErrors.tax_deductions}
                  inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Other Deductions"
                  type="number"
                  value={formData.other_deductions}
                  onChange={(e) =>
                    onInputChange(
                      'other_deductions',
                      e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                    )
                  }
                  disabled={mode === 'view'}
                  error={!!formErrors.other_deductions}
                  helperText={formErrors.other_deductions}
                  inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Late Deductions"
                  type="number"
                  value={formData.late_deductions}
                  onChange={(e) =>
                    onInputChange(
                      'late_deductions',
                      e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value, 10))
                    )
                  }
                  disabled={mode === 'view'}
                  error={!!formErrors.late_deductions}
                  helperText={formErrors.late_deductions}
                  inputProps={{ min: 0, step: 1, onFocus: (e) => e.target.select() }}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Absent Days"
                  type="number"
                  value={formData.absent_days}
                  onChange={(e) =>
                    onInputChange('absent_days', e.target.value === '' ? '' : Math.max(0, parseFloat(e.target.value)))
                  }
                  inputProps={{ min: 0, step: 0.1, onFocus: (e) => e.target.select() }}
                  disabled={mode === 'view'}
                  error={!!formErrors.absent_days}
                  helperText={formErrors.absent_days}
                />
              </Grid>

              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  label="Leave Days"
                  type="number"
                  value={formData.leave_days}
                  onChange={(e) =>
                    onInputChange('leave_days', e.target.value === '' ? '' : Math.max(0, parseFloat(e.target.value)))
                  }
                  inputProps={{ min: 0, step: 0.1, onFocus: (e) => e.target.select() }}
                  disabled={mode === 'view'}
                  error={!!formErrors.leave_days}
                  helperText={formErrors.leave_days}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Net Payable"
                  type="number"
                  value={formData.net_payable}
                  disabled
                  sx={{
                    '& .MuiInputBase-input.Mui-disabled': {
                      WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                      fontWeight: 'bold',
                      color: 'primary.main',
                      fontSize: '1.1rem',
                    },
                  }}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{mode === 'view' ? 'Close' : 'Cancel'}</Button>
        {mode !== 'view' && (
          <Button onClick={onSubmit} variant="contained">
            {mode === 'add' ? 'Create' : 'Update'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default PayrollForm;
